import { FormElementType } from '../types/form-element.types';

/**
 * AN-1851 — minimum size constraints for signature stamp boxes.
 *
 * Pure functions, deliberately free of Fabric.js, Angular and the DOM so the rules can be
 * tested directly rather than through a rendered canvas.
 *
 * Background: the server draws the signature stamp into the box the author placed, sizing
 * every element from one root value (`root = boxHeight / 7`). There is no floor on that
 * scale and no alternative layout, so a short box does not re-flow — it renders the same
 * four-row stamp with proportionally smaller type until it is unreadable.
 *
 * The minimum is **40pt**, set by @kurtulus on 2026-09-15. It is a product floor, and it sits
 * deliberately below the legibility anchor the earlier values were derived from.
 *
 * What that costs, stated plainly: the NAME is the largest element of the stamp at 1.1em, so
 * it renders at `1.1 × height / 7`. At the previous 45pt clamp that is 7.1pt — the
 * black-and-white print floor, which is exactly why 45 was chosen. At 40pt it is 6.3pt, so
 * the most important line of the stamp is now below the print floor too. What it buys is that
 * a lower clamp refuses fewer of the heights authors already use: the 60pt candidate refused
 * 22.2% of them and 45pt refused 15.6%, so 40pt refuses strictly less than 15.6% — the exact
 * figure has not been measured, and no number for it should be quoted until it is.
 *
 * The identifier, at 0.62em, was already under the print floor at every candidate height —
 * reaching it would need a 79pt box, which would refuse 59% of real sizes. Both trades are
 * recorded, not solved.
 *
 * See AN-1851 specification §6.6 and §6.8.
 */

/** Minimum height in PDF points for a box that carries the signature stamp (§6.6). */
export const MIN_STAMP_HEIGHT_PT = 40;

/**
 * The stamp's root value is the box height divided by this (§6.1: `root = boxHeight / 7`).
 * Every metric of the drawn stamp is a multiple of that root.
 */
export const STAMP_ROOT_DIVISOR = 7;

/**
 * Minimum width, as a multiple of the stamp's root value.
 *
 * Published by the backend on 2026-09-14, measured with the server's own font metrics
 * (9.5989, used as 9.6). Expressed in em rather than points deliberately: it survives a font
 * change and cannot drift between the two repositories. It decomposes across the stamp's
 * bottom row as rail 0.50 + padding 0.70 + statement 5.07 + gap 0.40 + frame-id slot 2.23 +
 * padding 0.70.
 *
 * The statement is measured against the LONGEST statement in the set (`Dijital imzalıdır`),
 * not the one a given signature happens to carry, so the bottom row cannot reflow when a
 * different assurance level is drawn into the same box. The frame-id slot is six characters
 * because it is sized for the widest supported format rather than today's value.
 *
 * Note the consequence of being em-based: because the root derives from the box height, the
 * minimum width is a function of the box's own height, not a constant. At the 40pt height
 * clamp it is 54.9pt; a 105pt-tall box needs 144pt, which is where a default-width box would
 * first start being widened by this rule.
 */
export const MIN_STAMP_WIDTH_EM = 9.6;

/** The stamp's root value in points for a given box height. */
export function stampRoot(boxHeight: number): number {
  return boxHeight / STAMP_ROOT_DIVISOR;
}

/**
 * Element types that carry the stamp and therefore take the minimum.
 *
 * Initials are deliberately absent. They carry no name, no statement and no identifier
 * (§6.7), so they have no minimum — and their own default height is 37pt, which is below the
 * stamp minimum at any of the candidate values.
 * Confirmed by the reviewer on 2026-09-14. Everything else (text, text input, checkbox,
 * radio, attachments list) is unconstrained exactly as before.
 */
export const STAMP_ELEMENT_TYPES: readonly FormElementType[] = [
  FormElementType.SIGNATURE,
  FormElementType.DIGITAL_SIGNATURE,
  FormElementType.BIOMETRIC_SIGNATURE
];

/**
 * An element's minimum size.
 *
 * Height is absolute points. Width is an em multiple, because the required width depends on
 * the box's own height — resolve it with `effectiveFloor`, never by reading `widthEm` directly.
 * Zero on either axis means "unconstrained".
 */
export interface SizeFloor {
  /** Absolute minimum height in PDF points. */
  height: number;
  /** Minimum width as a multiple of the stamp root. */
  widthEm: number;
  /**
   * Ceiling on the derived width floor, for a box already stored narrower than the formula
   * asks for. Absent means no ceiling. See `resolveFloor` — this is width's half of the
   * grandfathering rule, and without it the clamp would widen an existing box on a move.
   */
  widthCap?: number;
}

/** No constraint at all — the floor for every non-stamp element type. */
export const NO_FLOOR: SizeFloor = { height: 0, widthEm: 0 };

/** Optional host overrides, so a deployment can tune the minimum without a submodule release. */
export interface SizeConstraintOverrides {
  /** Absolute minimum height in PDF points. */
  minStampHeight?: number;
  /** Minimum width as a multiple of the stamp root — em, not points. */
  minStampWidthEm?: number;
}

export function isStampElement(type: FormElementType): boolean {
  return STAMP_ELEMENT_TYPES.includes(type);
}

/** The type's own minimum, before any grandfathering. */
export function stampMinimumFor(
  type: FormElementType,
  overrides?: SizeConstraintOverrides
): SizeFloor {
  if (!isStampElement(type)) {
    return NO_FLOOR;
  }

  return {
    widthEm: overrides?.minStampWidthEm ?? MIN_STAMP_WIDTH_EM,
    height: overrides?.minStampHeight ?? MIN_STAMP_HEIGHT_PT
  };
}

export interface FloorResolution {
  type: FormElementType;
  /**
   * The height this element was loaded with, in PDF points, for an element that came from a
   * saved configuration. Undefined for a box the user has just placed.
   */
  loadedHeight?: number;
  /** The width this element was loaded with, in PDF points. Undefined for a new placement. */
  loadedWidth?: number;
  /**
   * True when the designer is running in AN-1801's signing-placement mode, where the signer
   * places their own box during signing.
   */
  signingPlacement?: boolean;
  overrides?: SizeConstraintOverrides;
}

/**
 * Resolve the floor for one element, once, at the moment it first appears.
 *
 * Why this is not simply the type minimum: `handleObjectModified` fires on **move** as well
 * as resize, and writes `element.pdfPosition` — width and height included — unconditionally.
 * So an unconditional floor would mean that opening a template containing a 35pt box and
 * nudging it one point sideways silently grows it to the minimum, and the next save writes that
 * back.
 * That rewrites stored geometry through the side door, on templates, which recur — while every
 * test of "new placements are clamped" still passes. The clamp is specified for new placements
 * only, and existing geometry is explicitly to be left untouched (decision 14).
 *
 * Hence a per-element floor:
 *
 *   - newly placed                        -> the type minimum
 *   - loaded at or above the minimum      -> the type minimum
 *   - loaded below the minimum            -> its own loaded height, so it is never grown
 *                                            and cannot be shrunk further
 *   - signing-placement mode              -> a flat type minimum, never grandfathered
 *
 * The last case matters and is not a detail: AN-1801 provisions the signer's box server-side
 * at 1px, so grandfathering read literally would set its floor to 1px and switch the clamp
 * off precisely where a stamp is guaranteed to be drawn. Confirmed by the reviewer, and
 * flagged to @kurtulus as the one part of this that a signer will feel.
 */
export function resolveFloor(resolution: FloorResolution): SizeFloor {
  const { type, loadedHeight, loadedWidth, signingPlacement, overrides } = resolution;
  const minimum = stampMinimumFor(type, overrides);

  if (minimum.height === 0 && minimum.widthEm === 0) {
    return NO_FLOOR;
  }

  // Signing placement is always a new placement, whatever geometry it arrives with.
  if (signingPlacement) {
    return minimum;
  }

  const usable = (value: number | undefined): value is number =>
    typeof value === 'number' && isFinite(value) && value > 0;

  const floor: SizeFloor = { ...minimum };

  // Height: a box stored shorter than the minimum keeps its own height.
  if (usable(loadedHeight) && loadedHeight < minimum.height) {
    floor.height = loadedHeight;
  }

  // Width: the same rule, against the width the formula asks for AT THE HEIGHT IT WAS STORED
  // AT. Only 12 of the 230,539 boxes in production are affected, because the requirement
  // scales with height and short boxes need proportionally less width — but 12 is not zero,
  // and a template recurs, so without this the clamp would widen one of them on a move.
  if (usable(loadedWidth) && usable(loadedHeight)) {
    const required = minimum.widthEm * stampRoot(loadedHeight);
    if (loadedWidth < required) {
      floor.widthCap = loadedWidth;
    }
  }

  return floor;
}

/**
 * Resolve a floor into absolute points for a given rectangle.
 *
 * Width is derived from the height the rectangle will actually have once the height floor is
 * applied, because the stamp's root — and therefore every width in it — is a function of box
 * height. This is the only place `widthEm` should be turned into points.
 */
export function effectiveFloor(rect: ClampableRect, floor: SizeFloor): { width: number; height: number } {
  const height = Math.max(rect.height, floor.height || 0);

  let width = 0;
  if (floor.widthEm > 0) {
    width = floor.widthEm * stampRoot(height);
    if (typeof floor.widthCap === 'number') {
      width = Math.min(width, floor.widthCap);
    }
  }

  return { width, height };
}

export interface ClampableRect {
  x: number;
  y: number;
  width: number;
  height: number;
  angle?: number;
}

/**
 * Raise a rectangle to its floor, in PDF points.
 *
 * `x` and `y` are preserved, which is the correct anchor for all three resize handles the
 * designer keeps: middle-top pins the bottom edge, middle-right pins the left edge, and
 * top-right pins the bottom-left corner. In PDF coordinates (bottom-left origin) that is
 * (x, y) in every case, so growing width and height upward and rightward is what the user's
 * drag implies.
 *
 * `pageDimensions`, when supplied, keeps a clamped box on the page: growing a box that sits
 * against the top or right edge would otherwise push it off, and the caller's own bounds
 * check has already run by this point.
 */
export function clampRect(
  rect: ClampableRect,
  floor: SizeFloor,
  pageDimensions?: { width: number; height: number }
): ClampableRect {
  const effective = effectiveFloor(rect, floor);

  const clamped: ClampableRect = {
    ...rect,
    width: Math.max(rect.width, effective.width),
    height: Math.max(rect.height, effective.height)
  };

  if (clamped.width === rect.width && clamped.height === rect.height) {
    return { ...rect };
  }

  if (pageDimensions) {
    // Never grow beyond the page itself, then pull the origin back inside if the larger box
    // now overhangs. A box bigger than the page lands at the origin, which is the only
    // position that exists for it.
    clamped.width = Math.min(clamped.width, pageDimensions.width);
    clamped.height = Math.min(clamped.height, pageDimensions.height);
    clamped.x = Math.max(0, Math.min(clamped.x, pageDimensions.width - clamped.width));
    clamped.y = Math.max(0, Math.min(clamped.y, pageDimensions.height - clamped.height));
  }

  return clamped;
}

/** True when the rectangle is already at or above its floor, so nothing needs to change. */
export function satisfiesFloor(rect: ClampableRect, floor: SizeFloor): boolean {
  const effective = effectiveFloor(rect, floor);
  return rect.width >= effective.width && rect.height >= effective.height;
}
